import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { InputNumber } from 'primereact/inputnumber'
import { classNames } from 'primereact/utils'

import { useToastContext } from '../../../../../../contexts/toast'
import { AllowanceCharge } from '../../../../../../data/models'
import { useSelectFirstAndOnlyOption } from '../../../../../../hooks'
import { getHandleableError } from '../../../../../../utils/errors/isXiaError'
import { calculateBreakpointWidth } from '../../../../../../utils/helper/calculateBreakpointWidth'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../../../utils/helper/validateFormikFields'
import { ConfigurableComponent } from '../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../ui-config/context'
import {
  requestPostItemAllowanceChargesById,
  useInvoiceById,
} from '../../../../data'

type Props = {
  invoiceId: string
  itemId: string
}

type FormValues = {
  confirmationAlcType?: 'Surcharge' | 'Discount'
  confirmationDescription?: string
  confirmationPercentage?: number
  confirmationAmount?: number
}
type FormFieldName = keyof FormValues

export const AddInvoiceItemAllowanceCharge: React.FC<Props> = ({
  invoiceId,
  itemId,
}) => {
  const { t, i18n } = useTranslation()
  const { showToast } = useToastContext()
  const { uiConfig: config } = useConfigContext()

  const { data: invoice } = useInvoiceById(invoiceId)

  const [visible, setVisible] = useState(false)
  const [isRequestRunning, setIsRequestRunning] = useState(false)

  const { language } = i18n
  const currency = invoice?.Payload.OrderCurrency

  // ************************************************************
  // formik
  // ************************************************************
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      confirmationAlcType: undefined,
      confirmationDescription: undefined,
      confirmationPercentage: 0,
      confirmationAmount: 0,
    },
    validate: data => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          fieldName: 'confirmationAlcType',
          config:
            config.invoice.addInvoiceItemAllowanceCharge.confirmationAlcType,
        },
        {
          fieldName: 'confirmationDescription',
          config:
            config.invoice.addInvoiceItemAllowanceCharge
              .confirmationDescription,
        },
        {
          fieldName: 'confirmationPercentage',
          config:
            config.invoice.addInvoiceItemAllowanceCharge.confirmationPercentage,
        },
        {
          fieldName: 'confirmationAmount',
          config:
            config.invoice.addInvoiceItemAllowanceCharge.confirmationAmount,
        },
      ]

      validateFormikFields(fieldsToValidate, data, errors)

      return errors
    },
    onSubmit: async formData => {
      // TODO: onsubmit überall gleich aufbauen
      // deactivate the buttons
      setIsRequestRunning(true)

      try {
        // close the dialog and reset
        handleClose()

        // submit form
        await submitForm(formData)

        // mutate()

        // show success toast
        showToast({
          style: {},
          closable: true,
          severity: 'success',
          summary: t(
            'invoice.invoiceItems.table.edit.dialog.allowanceCharges.table.add.dialog.add.messages.success.summary',
            {
              type:
                formData.confirmationAlcType === 'Surcharge'
                  ? t('common.codes.alcType.surcharge')
                  : t('common.codes.alcType.discount'),
              description: formData.confirmationDescription,
            },
          ),
        })
      } catch (error) {
        let handleableError = getHandleableError(error)
        let errorMessage = handleableError.message

        // show error toast
        showToast({
          style: {},
          closable: true,
          severity: 'error',
          sticky: true,
          summary: errorMessage,
        })
      } finally {
        setIsRequestRunning(false)
      }
    },
  })

  const isFormFieldValid = (name: FormFieldName) =>
    !(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: FormFieldName) => {
    return (
      !isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    )
  }

  // submit form
  const submitForm = (formData: FormValues) => {
    const newAllowanceCharge: AllowanceCharge = {
      AlcType: formData.confirmationAlcType,
      ConfirmationAlcType: formData.confirmationAlcType,
      Description: {
        Code: 'Supplier',
        Text: formData.confirmationDescription,
      },
      ConfirmationDescription: formData.confirmationDescription,
      Percentage: formData.confirmationPercentage,
      ConfirmationPercentage: formData.confirmationPercentage,
      Amount: formData.confirmationAmount,
      ConfirmationAmount: formData.confirmationAmount,
    }
    // post for DespatchAdvice
    const response = requestPostItemAllowanceChargesById(
      invoiceId,
      itemId,
      newAllowanceCharge,
    )

    // return response header
    return response
  }

  // handleClose
  const handleClose = () => {
    formik.resetForm()
    setVisible(false)
  }

  // ************************************************************
  // components
  // ************************************************************
  // confirmationAlcType
  const confirmationAlcTypeLabel = (
    <label
      htmlFor="confirmationAlcType"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationAlcType'),
      })}
    ></label>
  )

  useSelectFirstAndOnlyOption(
    'confirmationAlcType',
    config.invoice.addInvoiceItemAllowanceCharge.confirmationAlcType.options,
    formik,
  )

  const confirmationAlcTypeComponent = (
    <Dropdown
      id="confirmationAlcType"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationAlcType'),
      })}
      value={formik.values.confirmationAlcType}
      onChange={formik.handleChange}
      optionLabel="code"
      optionValue="code"
    />
  )

  const confirmationAlcTypeErrorMsg = (
    <div>{getFormErrorMessage('confirmationAlcType')}</div>
  )

  const confirmationAlcTypeWrapper = (
    <ConfigurableComponent
      config={config.invoice.addInvoiceItemAllowanceCharge}
      component={confirmationAlcTypeComponent}
      label={confirmationAlcTypeLabel}
      errorMsg={confirmationAlcTypeErrorMsg}
      className="col-12"
    />
  )

  // confirmationDescription
  const confirmationDescriptionLabel = (
    <label
      htmlFor="confirmationDescription"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationDescription'),
      })}
    ></label>
  )

  useSelectFirstAndOnlyOption(
    'confirmationDescription',
    config.invoice.addInvoiceItemAllowanceCharge.confirmationDescription
      .options,
    formik,
  )

  const confirmationDescriptionComponent = (
    <Dropdown
      id="confirmationDescription"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationDescription'),
      })}
      value={formik.values.confirmationDescription}
      onChange={formik.handleChange}
      optionLabel="code"
      optionValue={`name.${language}`}
    />
  )

  const confirmationDescriptionErrorMsg = (
    <div>{getFormErrorMessage('confirmationDescription')}</div>
  )

  const confirmationDescriptionWrapper = (
    <ConfigurableComponent
      config={config.invoice.addInvoiceItemAllowanceCharge}
      component={confirmationDescriptionComponent}
      label={confirmationDescriptionLabel}
      errorMsg={confirmationDescriptionErrorMsg}
      className="col-12"
    />
  )

  // confirmationPercentage
  const confirmationPercentageLabel = (
    <label
      htmlFor="confirmationPercentage"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationPercentage'),
      })}
    ></label>
  )

  const confirmationPercentageComponent = (
    <InputNumber
      id="confirmationPercentage"
      value={formik.values.confirmationPercentage}
      onChange={e => formik.setFieldValue('confirmationPercentage', e.value)}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationPercentage'),
      })}
      min={0}
      suffix=" %"
      minFractionDigits={2}
      locale={language}
      showButtons
    />
  )

  const confirmationPercentageErrorMsg = (
    <div>{getFormErrorMessage('confirmationPercentage')}</div>
  )

  const confirmationPercentageWrapper = (
    <ConfigurableComponent
      config={config.invoice.addInvoiceItemAllowanceCharge}
      component={confirmationPercentageComponent}
      label={confirmationPercentageLabel}
      errorMsg={confirmationPercentageErrorMsg}
      className="col-12"
    />
  )

  // confirmationAmount
  const confirmationAmountLabel = (
    <label
      htmlFor="confirmationAmount"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationAmount'),
      })}
    ></label>
  )

  const confirmationAmountComponent = (
    <InputNumber
      id="confirmationAmount"
      mode="currency"
      showButtons
      currency={currency}
      value={formik.values.confirmationAmount}
      onChange={e => formik.setFieldValue('confirmationAmount', e.value)}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationAmount'),
      })}
      min={0}
      locale={language}
    />
  )

  const confirmationAmountErrorMsg = (
    <div>{getFormErrorMessage('confirmationAmount')}</div>
  )

  const confirmationAmountWrapper = (
    <ConfigurableComponent
      config={config.invoice.addInvoiceItemAllowanceCharge}
      component={confirmationAmountComponent}
      label={confirmationAmountLabel}
      errorMsg={confirmationAmountErrorMsg}
      className="col-12"
    />
  )

  // ************************************************************
  // dialog components
  // ************************************************************
  // footer
  const footer = (
    <div className="flex flex-wrap justify-content-between pt-3">
      <Button
        className="p-button-text ml-auto w-full md:w-auto mb-1 md:mb-0"
        label={t('common.buttons.cancel')}
        icon="pi pi-times"
        onClick={handleClose}
        type="button"
      />
      <Button
        label={t('common.buttons.add')}
        className="w-full md:w-auto"
        icon={'pi pi-plus'}
        type="submit"
        onClick={formik.submitForm}
        loading={isRequestRunning}
        disabled={!formik.dirty}
      />
    </div>
  )

  return (
    <>
      <Button
        label={t('common.buttons.add')}
        icon="pi pi-plus"
        className="p-button mb-2"
        onClick={() => {
          setVisible(true)
        }}
        visible={config.invoice.addInvoiceItemAllowanceCharge.enabled !== false}
        loading={isRequestRunning}
      />

      <Dialog
        header={t(
          'invoice.invoiceItems.table.edit.dialog.allowanceCharges.table.add.dialog.title',
        )}
        visible={visible}
        style={{ width: '30rem' }}
        breakpoints={{ [calculateBreakpointWidth(30)]: '90vw' }}
        footer={footer}
        onHide={handleClose}
        blockScroll
      >
        <div className="formgrid grid">
          {confirmationAlcTypeWrapper}

          {confirmationDescriptionWrapper}

          {confirmationPercentageWrapper}

          {confirmationAmountWrapper}
        </div>
      </Dialog>
    </>
  )
}
